import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCustomerById, clearError } from '../store/slices/customersSlice';
import { fetchInvoices } from '../store/slices/invoicesSlice';
import { fetchOrders } from '../store/slices/ordersSlice';
import { toast } from 'react-toastify';
import './CustomerDetail.css';

const CustomerDetail = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentCustomer, loading, error } = useSelector(state => state.customers);
  const customer = currentCustomer?.data || null;

  const { invoices, loading: invoicesLoading } = useSelector(state => state.invoices);
  const { items: orders, loading: ordersLoading } = useSelector(state => state.orders);

  // Fetch customer details on mount or when ID changes
  useEffect(() => {
    if (customerId) {
      dispatch(fetchCustomerById(customerId))
        .unwrap()
        .catch(err => {
          toast.error(err?.message || 'Failed to load customer details');
        });
    }
  }, [customerId, dispatch]);

  // Fetch invoices and orders filtered by customerId
  useEffect(() => {
    if (customerId) {
      dispatch(fetchInvoices({ customerId, limit: 100 }))
        .unwrap()
        .catch(err => console.error('Failed to load invoices:', err));

      dispatch(fetchOrders({ customerId, limit: 100 }))
        .unwrap()
        .catch(err => console.error('Failed to load orders:', err));
    }
  }, [customerId, dispatch]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error(error?.message || 'An error occurred');
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Fallback for loading
  if (loading) {
    return (
      <div className="customer-detail-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading customer details...</p>
        </div>
      </div>
    );
  }

  // Fallback if no customer found
  if (!customer) {
    return (
      <div className="customer-detail-container">
        <div className="empty-state">
          <p>Customer not found</p>
          <button onClick={() => navigate('/customers')}>Back to Customers</button>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };
  
  const formatCurrency = (v, currency = 'USD') => {
    if (v === null || v === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(v));
  };

  const getTotalBilled = () => invoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
  const getTotalPaid = () => invoices.reduce((sum, inv) => sum + (inv.paidAmount || 0), 0);
  const getTotalOutstanding = () => invoices.reduce((sum, inv) => sum + Math.max(0, (inv.total || 0) - (inv.paidAmount || 0)), 0);
  const getPendingInvoiceCount = () => invoices.filter(inv => inv.status === 'pending' || inv.status === 'overdue').length;

  const getInvoiceStatusClass = (status) => {
    const map = { paid: 'status-paid', pending: 'status-pending', overdue: 'status-overdue', partial: 'status-pending', draft: 'status-default', sent: 'status-default', cancelled: 'status-default' };
    return map[status] || 'status-default';
  };

  const getOrderStatusClass = (status) => {
    const map = { confirmed: 'status-paid', pending: 'status-pending', processing: 'status-pending', shipped: 'status-active', delivered: 'status-paid', cancelled: 'status-overdue' };
    return map[status] || 'status-default';
  };

  return (
    <div className="customer-detail-container">
      {/* Header */}
      <div className="customer-detail-header">
        <button className="btn-back" onClick={() => navigate(-1)}>← Back</button>
        <div className="customer-info-header">
          <span className="customer-avatar">{customer.avatar || '👤'}</span>
          <div>
            <h1>{customer.name || 'Unknown'}</h1>
            <p>{customer.email || '—'}</p>
          </div>
        </div>
        <span className={`status-badge ${customer.status === 'active' ? 'status-active' : 'status-inactive'}`}>
          {(customer.status || 'inactive').toUpperCase()}
        </span>
      </div>

      {/* Customer Info Grid */}
      <div className="customer-info-grid">
        <div className="info-card">
          <h3>Contact Information</h3>
          <div className="info-rows">
            <div className="info-row">
              <span className="label">Phone</span>
              <span className="value">{customer.phone || customer.phoneNumber || '—'}</span>
            </div>
            <div className="info-row">
              <span className="label">Email</span>
              <span className="value">{customer.email || '—'}</span>
            </div>
            <div className="info-row">
              <span className="label">Address</span>
              <span className="value">{customer.address || '—'}</span>
            </div>
            <div className="info-row">
              <span className="label">City</span>
              <span className="value">{customer.city || '—'}</span>
            </div>
          </div>
        </div>

        <div className="info-card">
          <h3>Business Information</h3>
          <div className="info-rows">
            <div className="info-row">
              <span className="label">Company Name</span>
              <span className="value">{customer.companyName || '—'}</span>
            </div>
            <div className="info-row">
              <span className="label">GSTIN</span>
              <span className="value">{customer.gstin || '—'}</span>
            </div>
            <div className="info-row">
              <span className="label">Member Since</span>
              <span className="value">{formatDate(customer.joinDate || customer.createdAt)}</span>
            </div>
          </div>
        </div>

        <div className="info-card">
          <h3>Account Summary</h3>
          <div className="summary-items">
            <div className="summary-item">
              <div className="summary-label">Total Billed</div>
              <div className="summary-value">{formatCurrency(getTotalBilled())}</div>
            </div>
            <div className="summary-item">
              <div className="summary-label">Total Paid</div>
              <div className="summary-value paid">{formatCurrency(getTotalPaid())}</div>
            </div>
            <div className="summary-item">
              <div className="summary-label">Outstanding</div>
              <div className={`summary-value ${getTotalOutstanding() > 0 ? 'outstanding' : ''}`}>
                {formatCurrency(getTotalOutstanding())}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoices Section */}
      <div className="detail-section">
        <div className="section-header">
          <div>
            <h2>Invoices ({invoices.length})</h2>
            <p>{getPendingInvoiceCount()} pending or overdue</p>
          </div>
        </div>

        {invoicesLoading ? (
          <div className="empty-state"><div className="spinner"></div><p>Loading invoices...</p></div>
        ) : invoices.length > 0 ? (
          <div className="bills-table-wrapper">
            <table className="bills-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Date</th>
                  <th>Due Date</th>
                  <th className="amount-col">Total</th>
                  <th className="amount-col">Paid</th>
                  <th className="amount-col">Balance</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => {
                  const balance = Math.max(0, (inv.total || 0) - (inv.paidAmount || 0));
                  return (
                    <tr key={inv._id}>
                      <td className="bill-id">{inv.invoiceNumber}</td>
                      <td>{formatDate(inv.invoiceDate)}</td>
                      <td>{formatDate(inv.dueDate)}</td>
                      <td className="amount-col">{formatCurrency(inv.total, inv.currency)}</td>
                      <td className="amount-col paid">{formatCurrency(inv.paidAmount, inv.currency)}</td>
                      <td className="amount-col">{formatCurrency(balance, inv.currency)}</td>
                      <td>
                        <span className={`bill-status ${getInvoiceStatusClass(inv.status)}`}>
                          {(inv.status || 'draft').toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state"><p>No invoices found</p></div>
        )}
      </div>

      {/* Orders Section */}
      <div className="detail-section">
        <div className="section-header">
          <div>
            <h2>Orders ({orders.length})</h2>
            <p>{orders.filter(o => o.status === 'pending' || o.status === 'processing').length} in progress</p>
          </div>
        </div>

        {ordersLoading ? (
          <div className="empty-state"><div className="spinner"></div><p>Loading orders...</p></div>
        ) : orders.length > 0 ? (
          <div className="bills-table-wrapper">
            <table className="bills-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Date</th>
                  <th>Items</th>
                  <th className="amount-col">Total</th>
                  <th>Payment</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <tr key={order._id}>
                    <td className="bill-id">{order.orderNumber}</td>
                    <td>{formatDate(order.createdAt)}</td>
                    <td>{(order.productItems || []).length} item(s)</td>
                    <td className="amount-col">{formatCurrency(order.totalAmount, order.currency)}</td>
                    <td>
                      <span className={`bill-status ${order.paymentStatus === 'paid' ? 'status-paid' : order.paymentStatus === 'pending' ? 'status-pending' : 'status-default'}`}>
                        {(order.paymentStatus || 'pending').toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <span className={`bill-status ${getOrderStatusClass(order.status)}`}>
                        {(order.status || 'pending').toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state"><p>No orders found</p></div>
        )}
      </div>
    </div>
  );
};

export default CustomerDetail;
